import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';

/**
 * NotFound, rendered for any unmatched route. Sets noindex/nofollow so
 * crawlers do not treat unknown SPA paths as duplicate homepage content,
 * and additionally tries to surface a 404 status hint to crawlers via a
 * <meta name="prerender-status-code" content="404"> tag.
 */
export default function NotFound() {
  useEffect(() => {
    // Standard hint for prerender / static-snapshot services.
    let el = document.head.querySelector('meta[name="prerender-status-code"]');
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'prerender-status-code');
      el.setAttribute('content', '404');
      el.setAttribute('data-seo', '1');
      document.head.appendChild(el);
    }
    return () => { if (el && el.parentNode) el.parentNode.removeChild(el); };
  }, []);

  return (
    <SiteLayout>
      <SEO
        title="Page not found, Keystone by CloudVoro"
        description="The page you requested does not exist on keystone.cloudvoro.com."
        path="/404"
        noindex
      />

      <section style={{
        background: PALETTE.cream, minHeight: '60vh',
        padding: '120px 32px 80px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ maxWidth: 640, textAlign: 'center' }} data-testid="not-found-page">
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 18,
          }}>
            404, Not found
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(40px, 6vw, 72px)',
            fontWeight: 700, color: PALETTE.navy, lineHeight: 1.05,
            letterSpacing: '-0.025em', margin: '0 0 18px',
          }}>
            That page <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>doesn’t exist.</em>
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: PALETTE.slate, margin: '0 0 32px' }}>
            The URL you requested isn’t one of our pages. It may have been moved, mistyped or never existed.
            Here are a few good places to start instead.
          </p>

          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center',
          }}>
            <Link to="/" data-testid="nf-home" style={{
              padding: '12px 22px', background: PALETTE.navy, color: PALETTE.paper,
              textDecoration: 'none', fontWeight: 600, fontSize: 14,
              letterSpacing: '0.02em',
            }}>Go home →</Link>
            <Link to="/site/features" data-testid="nf-features" style={{
              padding: '12px 22px', background: PALETTE.paper, color: PALETTE.navy,
              textDecoration: 'none', fontWeight: 600, fontSize: 14,
              letterSpacing: '0.02em', border: `1px solid ${PALETTE.border}`,
            }}>See features</Link>
            <Link to="/site/contact" data-testid="nf-contact" style={{
              padding: '12px 22px', background: PALETTE.paper, color: PALETTE.navy,
              textDecoration: 'none', fontWeight: 600, fontSize: 14,
              letterSpacing: '0.02em', border: `1px solid ${PALETTE.border}`,
            }}>Talk to us</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
